import { navigate } from '../core/router';
import { esc, setPageTitle, showErr } from './users-shared';
import { fetchTeams } from './teams-shared';
import { fetchClickerApps, fetchTeamClickerApps, saveTeamClickerApps, clickerAppLabel, } from '../clicker/clicker-apps-shared';
import { t } from '../ui/lang';
/**
 * Full-page create / edit team form (replaces the old showTeamFormModal). Rendered
 * for the `#!/teams/new` and `#!/teams/<id>/edit` routes (under the Users page nav).
 * Superadmin-only (the create/PATCH team + PUT availability endpoints are superadmin).
 *
 * Carries the team name AND the per-team clicker app availability list: one checkbox
 * per catalog app ("may this team use it"). Which task types the app runs (Deposit /
 * Payout) is shown read-only from the catalog — it is not chosen here. An empty list
 * means "all apps available" (backwards-compatible). On create the team is POSTed
 * first, then the availability list is PUT against its new id.
 */
export async function renderTeamForm(mode, teamId) {
    setPageTitle(mode === 'new' ? t('users.create_team_title') : t('users.edit_team_title'));
    const content = document.getElementById('content');
    content.innerHTML = `
        <div class="farm-devs-header">
            <button class="back-btn" id="tf-back">${t('users.back_to_teams')}</button>
            <h2 class="settings-title" id="tf-title">${mode === 'new' ? t('users.create_team_title') : t('users.edit_team_title')}</h2>
        </div>
        <div id="tf-body" class="user-form-page"><p class="empty">${t('common.loading')}</p></div>
    `;
    content.querySelector('#tf-back').addEventListener('click', () => navigate({ page: 'users', tab: 'teams' }));
    const body = content.querySelector('#tf-body');
    // Load the catalog (always) and, when editing, the team name + its available apps.
    let catalog = [];
    let teamName = '';
    let closeShift = false;
    let available = [];
    try {
        catalog = await fetchClickerApps();
        if (mode === 'edit' && teamId != null) {
            const team = (await fetchTeams()).find((t) => t.id === teamId);
            if (!team) {
                body.innerHTML = `<p class="empty">${t('users.team_not_found')}</p>`;
                return;
            }
            teamName = team.name;
            closeShift = team.close_shift;
            available = await fetchTeamClickerApps(teamId);
        }
    }
    catch {
        body.innerHTML = `<p class="empty">${t('common.failed_to_load')}</p>`;
        return;
    }
    if (mode === 'edit') {
        content.querySelector('#tf-title').textContent =
            t('users.edit_team_with_name', { name: teamName });
    }
    // Set of available app names from the loaded list; empty list = all allowed.
    const allowed = new Set(available);
    const listIsEmpty = available.length === 0;
    const rows = catalog.map((a) => {
        // One checkbox per app: is it available to this team? An empty list means
        // "all apps allowed", so it renders fully unchecked (the hint tells the admin
        // to leave everything blank to allow all). The task types the app supports are
        // shown read-only from the catalog — not chosen here.
        const checked = listIsEmpty ? false : allowed.has(a.name);
        const supports = [a.supports_deposit ? t('users.deposit') : null, a.supports_payout ? t('users.payout') : null]
            .filter(Boolean).join(' · ') || '—';
        return `
            <tr>
                <td>${esc(clickerAppLabel(a))}</td>
                <td style="color:var(--muted)">${supports}</td>
                <td style="text-align:center">
                    <input type="checkbox" class="tf-avail" data-app="${esc(a.name)}" ${checked ? 'checked' : ''} />
                </td>
            </tr>`;
    }).join('');
    body.innerHTML = `
        <form id="tf-form" class="team-form" novalidate>
            <div class="modal__field">
                <label>${t('users.team_name')}</label>
                <input id="tf-name" type="text" autocomplete="off" maxlength="40" placeholder="${t('users.team_name_placeholder')}"
                       value="${mode === 'edit' ? esc(teamName) : ''}" required />
            </div>
            <div class="settings-section">
                <h3 class="settings-section__title">${t('users.team_close_shift')}</h3>
                <label class="fe-check"><input id="tf-close-shift" type="checkbox"${closeShift ? ' checked' : ''} /> ${t('users.team_close_shift_hint')}</label>
            </div>
            <div class="settings-section">
                <h3 class="settings-section__title">${t('users.clicker_app_availability')}</h3>
                <p class="settings-hint">${t('users.clicker_app_availability_hint')}</p>
                ${catalog.length === 0
        ? `<p class="settings-hint">${t('users.no_clicker_apps')}</p>`
        : `<table class="access-matrix">
                        <thead><tr><th>${t('users.col_app')}</th><th>${t('users.col_supports')}</th><th>${t('users.col_available')}</th></tr></thead>
                        <tbody>${rows}</tbody>
                       </table>`}
            </div>
            <div id="tf-error" class="modal__error" style="display:none"></div>
            <div class="modal__actions">
                <button type="button" class="modal__cancel" id="tf-cancel">${t('common.cancel')}</button>
                <button type="submit" class="modal__submit">${mode === 'new' ? t('common.create') : t('common.save')}</button>
            </div>
        </form>
    `;
    const form = body.querySelector('#tf-form');
    const nameInput = body.querySelector('#tf-name');
    const errorEl = body.querySelector('#tf-error');
    body.querySelector('#tf-cancel').addEventListener('click', () => navigate({ page: 'users', tab: 'teams' }));
    nameInput.focus();
    const collectApps = () => {
        const apps = [];
        body.querySelectorAll('.tf-avail:checked').forEach((cb) => {
            apps.push(cb.dataset['app']);
        });
        return apps;
    };
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        errorEl.style.display = 'none';
        const name = nameInput.value.trim();
        if (!name) {
            showErr(errorEl, t('users.team_name_required'));
            return;
        }
        if (name.length > 40) {
            showErr(errorEl, t('users.team_name_too_long'));
            return;
        }
        const btn = form.querySelector('.modal__submit');
        btn.disabled = true;
        try {
            // Resolve the team id: create first in 'new' mode, reuse in 'edit'.
            let id = teamId;
            if (mode === 'new') {
                const res = await fetch('/api/teams', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name }),
                });
                const data = await res.json();
                if (!res.ok || data.id == null) {
                    showErr(errorEl, data.error ?? t('users.failed_to_create_team'));
                    return;
                }
                id = data.id;
            }
            else if (name !== teamName) {
                const res = await fetch(`/api/teams/${id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name }),
                });
                const data = await res.json();
                if (!res.ok) {
                    showErr(errorEl, data.error ?? t('users.failed_to_rename_team'));
                    return;
                }
            }
            const wantShift = body.querySelector('#tf-close-shift').checked;
            if (wantShift !== closeShift) {
                const res = await fetch(`/api/teams/${id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ close_shift: wantShift }),
                });
                const data = await res.json();
                if (!res.ok) {
                    showErr(errorEl, data.error ?? t('users.failed_to_save_team'));
                    return;
                }
            }
            // Persist the availability list against the (now-known) team id.
            await saveTeamClickerApps(id, collectApps());
            navigate({ page: 'users', tab: 'teams' });
        }
        catch (err) {
            showErr(errorEl, err instanceof Error ? err.message : t('users.network_error'));
        }
        finally {
            btn.disabled = false;
        }
    });
}
